import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TeacherSections.css';

const TeacherSectionLiveClasses = () => {
  const { t, i18n } = useTranslation('teacher');
  
  // Format date based on current language
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
  
  // Format day name
  const formatDayName = (day) => {
    return t(`liveClasses.availability.days.${day}`);
  };
  const [sessions, setSessions] = useState([
    {
      id: 1,
      studentName: 'Sarah Johnson',
      studentAvatar: 'SJ',
      subject: 'Advanced Speaking Practice',
      date: '2024-03-15',
      time: '15:00',
      duration: 60,
      status: 'upcoming',
      studentId: 101
    },
    {
      id: 2,
      studentName: 'Michael Chen',
      studentAvatar: 'MC',
      subject: 'IELTS Writing Practice',
      date: '2024-03-16',
      time: '10:00',
      duration: 90,
      status: 'upcoming',
      studentId: 102
    },
    {
      id: 3,
      studentName: 'Anna Lopez',
      studentAvatar: 'AL',
      subject: 'Grammar Review Session',
      date: '2024-03-14',
      time: '14:00',
      duration: 60,
      status: 'completed',
      studentId: 103
    }
  ]);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    date: '',
    time: '',
    duration: 60,
    subject: '',
    notes: ''
  });

  const [availability, setAvailability] = useState({
    monday: { start: '09:00', end: '17:00', available: true },
    tuesday: { start: '09:00', end: '17:00', available: true },
    wednesday: { start: '09:00', end: '17:00', available: true },
    thursday: { start: '09:00', end: '17:00', available: true },
    friday: { start: '09:00', end: '17:00', available: true },
    saturday: { start: '10:00', end: '15:00', available: false },
    sunday: { start: '10:00', end: '15:00', available: false }
  });

  const handleScheduleInputChange = (e) => {
    const { name, value } = e.target;
    setScheduleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScheduleSubmit = (e) => {
    e.preventDefault();
    
    // In a real app, this would create a new session
    const newSession = {
      id: Date.now(),
      studentName: 'Open Session',
      studentAvatar: 'OS',
      subject: scheduleForm.subject,
      date: scheduleForm.date,
      time: scheduleForm.time,
      duration: parseInt(scheduleForm.duration),
      status: 'upcoming',
      studentId: null
    };
    
    setSessions(prev => [...prev, newSession]);
    setScheduleForm({
      date: '',
      time: '',
      duration: 60,
      subject: '',
      notes: ''
    });
    setShowScheduleModal(false);
  };

  const handleAvailabilityChange = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const toggleAvailability = (day) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        available: !prev[day].available
      }
    }));
  };

  const joinClass = (sessionId) => {
    // In a real app, this would open the video call
    console.log(`Joining session ${sessionId}`);
    alert(`Joining session ${sessionId} - Video call would open here`);
  };

  const rescheduleClass = (sessionId) => {
    // In a real app, this would open reschedule modal
    console.log(`Rescheduling session ${sessionId}`);
    alert(`Rescheduling session ${sessionId}`);
  };

  const cancelClass = (sessionId) => {
    if (window.confirm(t('liveClasses.cancelConfirm'))) {
      setSessions(prev => prev.map(session => 
        session.id === sessionId 
          ? { ...session, status: 'cancelled' }
          : session
      ));
    }
  };

  const viewRecording = (sessionId) => {
    console.log(`Viewing recording for session ${sessionId}`);
    alert(`Viewing recording for session ${sessionId}`);
  };

  const sendFeedback = (sessionId) => {
    console.log(`Sending feedback for session ${sessionId}`);
    alert(`Sending feedback for session ${sessionId}`);
  };

  const getTodaySessions = () => {
    const today = new Date().toISOString().split('T')[0];
    return sessions.filter(session => session.date === today);
  };

  const getThisWeekSessions = () => {
    const today = new Date();
    const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
    const weekEnd = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    
    return sessions.filter(session => {
      const sessionDate = new Date(session.date);
      return sessionDate >= weekStart && sessionDate <= weekEnd;
    });
  };

  const getActiveStudents = () => {
    const activeSessions = sessions.filter(session => 
      session.status === 'upcoming' || session.status === 'completed'
    );
    return new Set(activeSessions.map(session => session.studentId)).size;
  };

  return (
    <div className="teacher-main-content">
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('liveClasses.title')}</h1>
        <p className="teacher-content-subtitle">{t('liveClasses.subtitle')}</p>
      </div>

      {/* Live Classes Overview */}
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">video_call</i>
            {t('liveClasses.liveClassesManagement')}
          </div>
          <div className="teacher-header-actions">
            <button 
              className="teacher-btn secondary" 
              onClick={() => setShowAvailabilityModal(true)}
            >
              <i className="material-icons">schedule</i>
              {t('liveClasses.setAvailability')}
            </button>
            <button 
              className="teacher-btn primary" 
              onClick={() => setShowScheduleModal(true)}
            >
              <i className="material-icons">add</i>
              {t('liveClasses.scheduleClass')}
            </button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="teacher-overview-grid">
          <div className="teacher-overview-card">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('liveClasses.todaysClasses')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">event</i>
              </div>
            </div>
            <div className="teacher-overview-value">{getTodaySessions().length}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">schedule</i>
              {t('liveClasses.scheduledToday')}
            </div>
          </div>

          <div className="teacher-overview-card success">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('liveClasses.thisWeek')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">schedule</i>
              </div>
            </div>
            <div className="teacher-overview-value">{getThisWeekSessions().length}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              {t('liveClasses.totalSessions')}
            </div>
          </div>

          <div className="teacher-overview-card accent">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('liveClasses.activeStudents')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">people</i>
              </div>
            </div>
            <div className="teacher-overview-value">{getActiveStudents()}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              {t('liveClasses.thisMonth')}
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Grid */}
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">video_call</i>
            {t('liveClasses.bookedSessions')}
          </div>
        </div>

        <div className="teacher-sessions-grid">
          {sessions.map((session) => (
            <div key={session.id} className={`teacher-session-card ${session.status}`}>
              <div className="session-info">
                <div className="student-avatar">{session.studentAvatar}</div>
                <div className="session-details">
                  <h4 className="session-subject">{session.subject}</h4>
                  <p className="student-name">{session.studentName}</p>
                  <p className="session-time">
                    {formatDate(session.date)} {t('common.at')} {session.time} ({session.duration} {t('liveClasses.duration').toLowerCase()})
                  </p>
                  <span className={`status-badge ${session.status}`}>
                    {session.status === 'upcoming' ? t('liveClasses.upcoming') : 
                     session.status === 'completed' ? t('liveClasses.completed') : t('liveClasses.cancelled')}
                  </span>
                </div>
              </div>
              <div className="session-actions">
                {session.status === 'upcoming' && (
                  <>
                    <button 
                      className="teacher-btn success" 
                      onClick={() => joinClass(session.id)}
                    >
                      <i className="material-icons">video_call</i>
                      {t('liveClasses.join')}
                    </button>
                    <button 
                      className="teacher-btn secondary" 
                      onClick={() => rescheduleClass(session.id)}
                    >
                      <i className="material-icons">schedule</i>
                      {t('liveClasses.reschedule')}
                    </button>
                    <button 
                      className="teacher-btn danger" 
                      onClick={() => cancelClass(session.id)}
                    >
                      <i className="material-icons">cancel</i>
                      {t('liveClasses.cancel')}
                    </button>
                  </>
                )}
                {session.status === 'completed' && (
                  <>
                    <button 
                      className="teacher-btn secondary" 
                      onClick={() => viewRecording(session.id)}
                    >
                      <i className="material-icons">play_circle</i>
                      {t('liveClasses.recording')}
                    </button>
                    <button 
                      className="teacher-btn secondary" 
                      onClick={() => sendFeedback(session.id)}
                    >
                      <i className="material-icons">rate_review</i>
                      {t('liveClasses.feedback')}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Schedule Class Modal */}
      {showScheduleModal && (
        <div className="teacher-modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="teacher-modal-close" 
              onClick={() => setShowScheduleModal(false)}
            >
              ×
            </button>
            
            <h2>{t('liveClasses.scheduleLiveClass')}</h2>
            
            <form className="teacher-form" onSubmit={handleScheduleSubmit}>
              <div className="teacher-form-group">
                <label htmlFor="sessionDate">{t('liveClasses.scheduleForm.date')} *</label>
                <input
                  type="date"
                  id="sessionDate"
                  name="date"
                  value={scheduleForm.date}
                  onChange={handleScheduleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="sessionTime">{t('liveClasses.scheduleForm.time')} *</label>
                <input
                  type="time"
                  id="sessionTime"
                  name="time"
                  value={scheduleForm.time}
                  onChange={handleScheduleInputChange}
                  required
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="sessionDuration">{t('liveClasses.scheduleForm.duration')} *</label>
                <select
                  id="sessionDuration"
                  name="duration"
                  value={scheduleForm.duration}
                  onChange={handleScheduleInputChange}
                  required
                >
                  <option value="30">{t('liveClasses.scheduleForm.durationOptions.30')}</option>
                  <option value="60">{t('liveClasses.scheduleForm.durationOptions.60')}</option>
                  <option value="90">{t('liveClasses.scheduleForm.durationOptions.90')}</option>
                  <option value="120">{t('liveClasses.scheduleForm.durationOptions.120')}</option>
                </select>
              </div>

              <div className="teacher-form-group">
                <label htmlFor="sessionSubject">{t('liveClasses.scheduleForm.subject')} *</label>
                <input
                  type="text"
                  id="sessionSubject"
                  name="subject"
                  value={scheduleForm.subject}
                  onChange={handleScheduleInputChange}
                  required
                  placeholder={t('liveClasses.scheduleForm.subjectPlaceholder')}
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="sessionNotes">{t('liveClasses.scheduleForm.notes')}</label>
                <textarea
                  id="sessionNotes"
                  name="notes"
                  value={scheduleForm.notes}
                  onChange={handleScheduleInputChange}
                  rows="3"
                  placeholder={t('liveClasses.scheduleForm.notesPlaceholder')}
                />
              </div>

              <div className="teacher-form-actions">
                <button type="submit" className="teacher-btn primary">
                  <i className="material-icons">schedule</i>
                  {t('liveClasses.scheduleForm.save')}
                </button>
                <button 
                  type="button" 
                  className="teacher-btn secondary"
                  onClick={() => setShowScheduleModal(false)}
                >
                  {t('liveClasses.scheduleForm.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Availability Modal */}
      {showAvailabilityModal && (
        <div className="teacher-modal-overlay" onClick={() => setShowAvailabilityModal(false)}>
          <div className="teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="teacher-modal-close" 
              onClick={() => setShowAvailabilityModal(false)}
            >
              ×
            </button>
            
            <h2>{t('liveClasses.availability.title')}</h2>
            
            <div className="availability-grid">
              {Object.entries(availability).map(([day, schedule]) => (
                <div key={day} className="availability-day">
                  <div className="day-header">
                    <label className="day-checkbox">
                      <input
                        type="checkbox"
                        checked={schedule.available}
                        onChange={() => toggleAvailability(day)}
                      />
                      <span className="day-name">{formatDayName(day)}</span>
                    </label>
                  </div>
                  
                  {schedule.available && (
                    <div className="day-schedule">
                      <div className="time-input">
                        <label>{t('liveClasses.availability.startTime')}</label>
                        <input
                          type="time"
                          value={schedule.start}
                          onChange={(e) => handleAvailabilityChange(day, 'start', e.target.value)}
                        />
                      </div>
                      <div className="time-input">
                        <label>{t('liveClasses.availability.endTime')}</label>
                        <input
                          type="time"
                          value={schedule.end}
                          onChange={(e) => handleAvailabilityChange(day, 'end', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="teacher-form-actions">
              <button 
                type="button" 
                className="teacher-btn primary"
                onClick={() => setShowAvailabilityModal(false)}
              >
                <i className="material-icons">save</i>
                {t('liveClasses.availability.save')}
              </button>
              <button 
                type="button" 
                className="teacher-btn secondary"
                onClick={() => setShowAvailabilityModal(false)}
              >
                {t('liveClasses.availability.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSectionLiveClasses; 